import UseContext from 'hooks/useContext';
import Button from 'components/button';
import Banner from 'components/banner/banner';
import { fillText } from 'components/perimeter/perimeterText';
import {
  NOTIFICATION_TYPES,
  allowsPerimeterLevel,
  toggleNotificationLevel,
} from 'components/perimeter/memberNotifications';

const TITLE_ID = 'contact-notifications-title';
const COLUMNS = ['perimeter', 'all'];

// The « Notifications » block of the contact screen. Like the perimeters
// block, it only shows the draft: the screen owns it and sends it on save.
const CompanyContactNotifications = ({
  status,
  levels,
  isPerimeterColumnDisabled,
  perimeterHint,
  error,
  onChange,
  onRetry,
}) => {
  const { language } = UseContext();
  const t = (key) =>
    language.getValue(`companyContacts.edit.notifications.${key}`, '');

  const isColumnDisabled = (type, column) =>
    column === 'perimeter' &&
    (isPerimeterColumnDisabled || !allowsPerimeterLevel(type));

  return (
    <div className={'flex flex-col gap-2'}>
      <section
        aria-labelledby={TITLE_ID}
        data-testid={'contact-notifications'}
        className={'rounded-lg border border-gray-200'}
      >
        <div className={'px-4 py-3'}>
          <div id={TITLE_ID} className={'text-gray-900'}>
            {t('title')}
          </div>
          {status === 'loading' ? (
            <div className={'text-gray-500'}>{t('loading')}</div>
          ) : null}
        </div>
        {status === 'error' ? (
          <div
            className={
              'flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-3'
            }
          >
            <div className={'text-gray-500'}>{t('loadError')}</div>
            <Button size={'SM'} color={'SECONDARY'} onClick={onRetry}>
              {t('retry')}
            </Button>
          </div>
        ) : null}
        {status === 'ready' && levels ? (
          <table className={'w-full border-t border-gray-100 text-left'}>
            <thead>
              <tr className={'text-xs uppercase text-gray-500'}>
                <th scope={'col'} className={'px-4 py-2 font-medium'}>
                  {t('name')}
                </th>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope={'col'}
                    className={'w-24 px-2 py-2 text-center font-medium'}
                  >
                    {t(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {NOTIFICATION_TYPES.map((type) => (
                <tr
                  key={type}
                  className={'border-t border-dashed border-gray-100'}
                >
                  <th scope={'row'} className={'px-4 py-2 font-normal'}>
                    <div className={'text-gray-900'}>
                      {t(`types.${type}.label`)}
                    </div>
                    <div className={'truncate text-gray-500'}>
                      {t(`types.${type}.description`)}
                    </div>
                  </th>
                  {COLUMNS.map((column) => {
                    const isDisabled = isColumnDisabled(type, column);
                    return (
                      <td key={column} className={'px-2 py-2 text-center'}>
                        <input
                          type={'checkbox'}
                          aria-label={fillText(t('checkboxLabel'), {
                            type: t(`types.${type}.label`),
                            column: t(column),
                          })}
                          checked={levels[type] === column}
                          disabled={isDisabled}
                          onChange={() =>
                            onChange(
                              type,
                              toggleNotificationLevel(levels[type], column),
                            )
                          }
                          className={`checkbox-primary ${
                            isDisabled ? 'cursor-not-allowed opacity-50' : ''
                          }`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {status === 'ready' && perimeterHint ? (
          <div
            className={
              'border-t border-gray-100 px-4 py-2 text-xs text-gray-500'
            }
            data-testid={'contact-notifications-hint'}
          >
            {perimeterHint}
          </div>
        ) : null}
      </section>
      {error ? <Banner type={'alert'}>{error}</Banner> : null}
    </div>
  );
};

export default CompanyContactNotifications;
